<!-- eslint-disable vue/html-self-closing -->
<template>
  <div
    ref="dropdown"
    class="gf-dropdown"
    :class="{ '--opened': showDropdown, '--no-carret': noCarret }"
  >
    <gf-button
      v-if="isCustomButton"
      ref="button"
      class="gf-dropdown__toggle"
      variant="secondary"
      :disabled="disabled"
      :before-icon="btnIcon"
      :text="btnText"
      :show-only-icon-mobile="true"
      @click="toggle()"
    ></gf-button>
    <button
      v-else
      ref="button"
      class="gf-dropdown__toggle"
      :class="btnClass"
      type="button"
      :disabled="disabled"
      @click="toggle()"
    >
      <i
        v-if="btnIcon"
        :class="btnIcon"
      ></i>
      <img
        v-if="btnImg"
        :src="btnImg"
        loading="lazy"
      />
      {{ btnText }}
    </button>
    <div
      ref="wrapper"
      class="gf-dropdown__wrapper"
      :class="'--' + position"
    >
      <div class="gf-dropdown__body">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import GfButton from '@shared/shared-components/shared/components/GfButton';

export default {
  name: 'GfDropdown',
  components: {
    GfButton
  },
  props: {
    position: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value)
    },
    btnClass: {
      type: String,
      default: 'btn btn-sm btn-primary'
    },
    btnText: {
      type: String
    },
    btnIcon: {
      type: String
    },
    btnImg: {
      type: String
    },
    noCarret: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    useCustomButton: {
      default: false
    }
  },
  data() {
    return {
      showDropdown: false
    };
  },
  computed: {
    isCustomButton() {
      return this.useCustomButton === true || this.useCustomButton === 'true';
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(this.getIntersection);
    document.addEventListener('click', (e) => {
      this.closeOnAway(e);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeOnAway());
    this.observer.disconnect();
  },
  methods: {
    getIntersection([{ isIntersecting, target, boundingClientRect }]) {
      if (isIntersecting) {
        if (boundingClientRect.left < 0) {
          if (target.classList.contains('--right')) {
            target.classList.replace('--right', '--left');
          }
        } else if (boundingClientRect.right > document.body.offsetWidth) {
          if (target.classList.contains('--left')) {
            target.classList.replace('--left', '--right');
          }
        }
      }
    },
    toggle() {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.observer.observe(this.$refs.wrapper);
      } else {
        this.observer.disconnect();
      }
    },
    close() {
      this.showDropdown = false;
    },
    closeOnAway(event) {
      if (
        !event ||
        event.target === this.$refs.dropdown ||
        event.target.closest('.gf-dropdown__body')
      ) {
        return;
      }
      if (this.isCustomButton) {
        if (event.target !== this.$refs.button?.$el) {
          this.close();
        }
      } else if (event.target !== this.$refs.button) {
        this.close();
      }
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-dropdown';

@media screen and (prefers-color-scheme: light) {
  body {
    --gfd-bg: rgb(255 255 255 / 70%);
    --gfd-bs: rgb(0 0 0 / 10%);
  }

  body.--theme-dark {
    --gfd-bg: rgb(26 29 31 / 70%) !important;
    --gfd-bs: rgb(75 75 77 / 38%) !important;
  }
}

@media screen and (prefers-color-scheme: dark) {
  body {
    --gfd-bg: rgb(26 29 31 / 70%);
    --gfd-bs: rgb(75 75 77 / 38%);
  }

  body.--theme-light {
    --gfd-bg: rgb(255 255 255 / 70%) !important;
    --gfd-bs: rgb(0 0 0 / 10%) !important;
  }
}

#{$this} {
  position: relative;
  display: block !important;

  // @media screen and (prefers-color-scheme: light) {
  //   --gfd-bg: rgb(255 255 255 / 70%);
  //   --gfd-bs: rgb(0 0 0 / 10%);
  // }

  // @media screen and (prefers-color-scheme: dark) {
  //   --gfd-bg: rgb(26 29 31 / 70%);
  //   --gfd-bs: rgb(75 75 77 / 38%);
  // }

  #{$this}__wrapper {
    position: absolute;
    padding: calc(var(--gfd-p, 1rem) / 2) var(--gfd-p, 1rem);
    background: var(--gfd-bg, rgb(255 255 255 / 65%));
    border-radius: var(--gfd-br, 0.4rem);
    box-shadow: 0 0 2px 1px var(--gfd-bs);
    opacity: 0;
    z-index: 33;
    backdrop-filter: saturate(180%) blur(18px);
    transform: translateY(-20px);
    transition: clip-path 0.2s cubic-bezier(0, 0.55, 0.45, 1), transform 0.2s linear,
      opacity 0.1s linear;
    pointer-events: none;
    will-change: transform, opacity;

    &.--left {
      left: 0;
      right: auto;
      clip-path: inset(0 100% 100% 0 round var(--gfd-br, 0.4rem));
    }

    &.--right {
      right: 0;
      left: auto;
      clip-path: inset(0 0 100% 100% round var(--gfd-br, 0.4rem));
    }
  }

  #{$this}__body {
    min-width: var(--gfd-minw, auto);
    max-width: var(--gfd-maxw, auto);
    transform: scale(1.06);
    transition: transform 0.15s ease;
    will-change: transform;
  }

  #{$this}__wrapper-item {
    display: block;
    width: 100%;
  }

  #{$this}__toggle {
    //font-size: 16px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    text-decoration: none;

    &::after {
      content: '';
      display: inline-block;
      margin-left: 8px;
      vertical-align: 2px;
      border-top: 4px solid;
      border-right: 4px solid transparent;
      border-bottom: 0;
      border-left: 4px solid transparent;
      transition: transform 0.15s ease;
    }

    i,
    img {
      pointer-events: none;
    }

    > img {
      width: var(--gfd-img-size, 25px);
      height: var(--gfd-img-size, 25px);
      border-radius: 100%;
    }
  }

  &.--opened {
    #{$this}__wrapper {
      opacity: 1;
      clip-path: inset(-30px -30px -30px -30px round var(--gfd-br, 0.4rem));
      transform: translateY(0);
      transition: clip-path 0.2s cubic-bezier(0, 0.55, 0.45, 1), transform 0.2s ease,
        opacity 0.1s linear;
      pointer-events: all;
    }

    #{$this}__body {
      transform: none;
      transition: transform 0.3s cubic-bezier(0.31, 0.48, 0.2, 1.04);
    }

    #{$this}__toggle::after {
      transform: rotate(180deg);
    }
  }

  &.--no-carret {
    #{$this}__toggle::after {
      display: none;
    }
  }
}
</style>
